import { Injectable } from '@angular/core';
import { HttpContext, HttpHeaders } from '@angular/common/http';
import { map } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from '@environments/environment';
import { RefreshTokenResponse } from '@app/core/auth/auth.model';
import { BYPASS_INTERCEPT } from '@app/core/interceptors/auth/auth-interceptor';
import { SessionStorageService } from '@app/core/auth/session-storage';
import { HttpService } from '@app/shared/services/http';
import { CustomAttribute } from '@app/core/auth/auth.model';

type RequiredClaims = Pick<
  CustomAttribute,
  'hospitalCode' | 'custom:x-uid' | 'iss' | 'token_use' | 'exp'
>;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private httpService: HttpService,
    private sessionService: SessionStorageService,
  ) {}

  public tokenRefresh() {
    const applicationClientId: string =
      this.sessionService.getApplicationClientId() || '';
    const hospitalCode: string = this.sessionService.getHospitalCode() || '';
    const refreshToken: string = this.sessionService.getRefreshToken() || '';
    const cognitoTokenEndPoint = environment.tokenEndPoint.replace(
      /{hospitalCode}/g,
      hospitalCode,
    );
    const url = `${cognitoTokenEndPoint}?grant_type=refresh_token&client_id=${applicationClientId}&refresh_token=${refreshToken}`;
    return this.httpService
      .post<RefreshTokenResponse>(url, null, {
        headers: new HttpHeaders({
          'Content-Type': 'application/x-www-form-urlencoded',
        }),
        context: new HttpContext().set(BYPASS_INTERCEPT, true),
      })
      .pipe(
        map((res) => {
          this.sessionService.saveAccessToken(res.access_token);
          this.sessionService.saveIdToken(res.id_token);
          return res;
        }),
      );
  }

  public verifyIdToken(idToken: string): boolean {
    try {
      const decoded = this.decodeIdToken(idToken);
      if (!decoded) throw new Error('decode failed');
      if (decoded.token_use !== 'id') throw new Error('decode failed');
      const now = Math.floor(Date.now() / 1000);
      if (decoded.exp < now) throw new Error('token expired');
      return true;
    } catch {
      console.warn('トークン検証失敗');
      return false;
    }
  }

  public isAdminUser(idToken?: string): boolean {
    try {
      const token = idToken ?? this.sessionService.getIdToken();
      if (!token) throw new Error('missing idToken');
      const decodedIdToken = this.decodeIdToken(token);
      if (!decodedIdToken) throw new Error('decode failed');
      return decodedIdToken.hospitalCode === environment.adminHospitalCode;
    } catch {
      console.warn('管理者検証失敗');
      return false;
    }
  }

  public decodeIdToken(idToken: string): CustomAttribute | null {
    try {
      const decoded = jwtDecode<CustomAttribute>(idToken);
      if (!this.hasRequiredClaims(decoded)) {
        console.warn('IDトークンの必須クレームが不足しています');
        return null;
      }
      return decoded;
    } catch {
      console.warn('IDトークンのデコードに失敗');
      return null;
    }
  }

  private hasRequiredClaims(
    c: Partial<CustomAttribute> | null,
  ): c is CustomAttribute & RequiredClaims {
    return (
      !!c &&
      typeof c.hospitalCode === 'string' &&
      c.hospitalCode.length > 0 &&
      typeof c['custom:x-uid'] === 'string' &&
      c['custom:x-uid'].length > 0 &&
      typeof c.iss === 'string' &&
      c.iss.length > 0 &&
      typeof c.token_use === 'string' &&
      typeof c.exp === 'number'
    );
  }
}
